"use client";

import React, { useState } from "react";
import { ChevronLeft, ChevronRight, MessageSquareText, Quote, User } from "lucide-react";

import { Section, Typography } from "@/components/ui";
import { cn } from "@/lib/utils";

export interface TestimonialItem {
  /** The quote itself, without surrounding quotation marks. */
  text: string;
  name: string;
  role: string;
  /** Whole stars shown above the quote. Defaults to 5. */
  rating?: number;
}

interface TestimonialsSectionProps {
  id?: string;
  sectionLabel?: string;
  title: string;
  subtitle?: React.ReactNode;
  items: TestimonialItem[];
  className?: string;
  /** Optional content below the quote panel (e.g. a CTA). */
  footer?: React.ReactNode;
}

/**
 * Featured testimonial panel — quote card beside a decorative gradient panel.
 * With more than one item it becomes a slider; a single item renders static.
 */
export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({
  id,
  sectionLabel,
  title,
  subtitle,
  items,
  className = "",
  footer,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const total = items.length;
  const isSlider = total > 1;
  const active = items[activeIndex];

  const go = (next: number) => setActiveIndex((next + total) % total);

  return (
    <Section id={id} variant="default" className={className}>
      <div className="text-center mb-16 lg:mb-20 max-w-3xl mx-auto">
        {sectionLabel ? <span className="section-label">{sectionLabel}</span> : null}
        <Typography variant="h2" as="h2" className="mb-3 text-4xl font-bold">
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body" as="p" className="text-text-secondary">
            {subtitle}
          </Typography>
        ) : null}
      </div>

      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm lg:grid-cols-[1.3fr_1fr]">
          <div
            className="flex flex-col justify-center p-8 md:p-12"
            aria-live={isSlider ? "polite" : undefined}
          >
            <Quote
              className="mb-4 h-10 w-10 text-secondary/20"
              fill="currentColor"
              aria-hidden="true"
            />

            <div className="text-amber-400 text-base mb-3 tracking-wider">
              {"★".repeat(active.rating ?? 5)}
            </div>
            <Typography
              variant="body"
              as="blockquote"
              className="italic text-text-primary leading-relaxed mb-6"
            >
              &quot;{active.text}&quot;
            </Typography>

            <div className="flex items-center gap-3 border-t border-gray-100 pt-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary/10 text-secondary">
                <User className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <Typography
                  variant="body"
                  as="p"
                  className="text-sm font-semibold text-text-primary"
                >
                  {active.name}
                </Typography>
                <Typography variant="body" as="p" className="text-xs text-text-secondary">
                  {active.role}
                </Typography>
              </div>
            </div>

            {isSlider ? (
              <div className="mt-8 flex items-center gap-4 border-t border-gray-100 pt-5">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => go(activeIndex - 1)}
                    aria-label="Previous testimonial"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-text-secondary transition-colors hover:border-secondary/40 hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => go(activeIndex + 1)}
                    aria-label="Next testimonial"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-text-secondary transition-colors hover:border-secondary/40 hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>

                <div className="flex gap-1.5">
                  {items.map((item, index) => (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => setActiveIndex(index)}
                      aria-label={`Show testimonial from ${item.name}`}
                      aria-current={index === activeIndex}
                      className={cn(
                        "h-2 rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40",
                        index === activeIndex
                          ? "w-6 bg-secondary"
                          : "w-2 bg-gray-300 hover:bg-gray-400"
                      )}
                    />
                  ))}
                </div>

                <Typography
                  variant="small"
                  as="p"
                  className="ml-auto text-xs tabular-nums text-text-secondary/75"
                >
                  {activeIndex + 1} / {total}
                </Typography>
              </div>
            ) : null}
          </div>

          <div className="relative hidden items-center justify-center bg-gradient-to-br from-secondary/5 to-transparent p-10 lg:flex">
            <div className="relative flex h-64 w-64 items-center justify-center rounded-full border border-secondary/10 bg-white/60">
              <span
                className="absolute -top-1 right-6 h-3 w-3 rounded-full bg-secondary"
                aria-hidden="true"
              />
              <span
                className="absolute -bottom-1 left-6 h-3 w-3 rounded-full bg-secondary/40"
                aria-hidden="true"
              />
              <div className="flex h-28 w-28 items-center justify-center rounded-2xl bg-gradient-to-br from-secondary to-primary shadow-lg">
                <MessageSquareText className="h-12 w-12 text-white" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {footer ? <div className="mt-10 flex justify-center">{footer}</div> : null}
    </Section>
  );
};
